import { useCallback, useEffect, useMemo, useState } from "react";
import { parseInstant } from "../../utils/dateUtils";
import { adminStorage } from "../../features/admin/service";
import { apiClient } from "../../lib/http/apiClient";
import { parseApiError } from "../../lib/http/apiErrors";
import ConfirmModal from "../../components/admin/ConfirmModal";
import { useCountries, useStates, useCities } from "../../features/geo/hooks";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";

interface OptionSet {
  type: string;
  items: any[];
  updated_at: string | null;
}

type ItemKind = "string" | "object";

const NUMERIC_FIELDS = new Set(["min", "max", "min_pax", "max_pax"]);

// Known field layouts for built-in option types — used so the editor renders
// the right inputs even when an option set is currently empty.
const FIELD_SCHEMAS: Record<string, string[]> = {
  activities: ["value", "label", "description"],
  seller_types: ["value", "label", "description"],
  travel_styles: ["value", "label", "description"],
  personalities: ["value", "label", "description"],
  pax_tiers: ["value", "label", "min_pax", "max_pax"],
  budget_presets: ["label", "min", "max"],
};

// "countries"/"cities" are no longer managed here — onboarding sources those
// from /api/v1/geo (full country/state/city dataset) instead.
const STRING_TYPES = new Set(["tags", "languages"]);

function fieldLabel(field: string): string {
  return field
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Read-only browser over /api/v1/geo — this is the live dataset onboarding
// uses for country/state/city selection (not stored in onboarding_options).
function GeoDataBrowser() {
  const { countries, loading: countriesLoading } = useCountries();
  const [search, setSearch] = useState("");
  const [selectedIso2, setSelectedIso2] = useState("");
  const { states, loading: statesLoading } = useStates(selectedIso2);
  const [selectedStateIso, setSelectedStateIso] = useState("");
  const { cities, loading: citiesLoading } = useCities(selectedIso2, selectedStateIso || undefined);

  const filteredCountries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter((c) => c.name.toLowerCase().includes(q) || c.iso2.toLowerCase() === q);
  }, [countries, search]);

  const selectedCountry = countries.find((c) => c.iso2 === selectedIso2);

  return (
    <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 mb-6">
      <h2 className="text-sm font-semibold text-white mb-1">Geo Data (read-only)</h2>
      <p className="text-gray-500 text-xs mb-4">
        Countries, states, and cities are sourced live from <span className="font-mono">/api/v1/geo</span>{" "}
        ({countries.length} countries available). They are fetched dynamically based on user selections and are no longer managed in Option Sets. You can browse the available geo data below; it isn't editable from this page.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Country</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search countries…"
            className="w-full px-3 py-2 mb-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
          />
          <select
            value={selectedIso2}
            onChange={(e) => { setSelectedIso2(e.target.value); setSelectedStateIso(""); }}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            size={8}
          >
            {countriesLoading && <option><Spinner /></option>}
            {filteredCountries.map((c) => (
              <option key={c.iso2} value={c.iso2}>{c.flag} {c.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">
            States {selectedCountry ? `(${selectedCountry.name})` : ""}
          </label>
          <select
            value={selectedStateIso}
            onChange={(e) => setSelectedStateIso(e.target.value)}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            size={8}
          >
            <option value="">All states / no state filter</option>
            {statesLoading && <option disabled><Spinner /></option>}
            {states.map((s) => (
              <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
            ))}
          </select>
          {!selectedIso2 && <p className="text-[11px] text-gray-600 mt-1">Select a country to see states.</p>}
          {selectedIso2 && !statesLoading && states.length === 0 && (
            <p className="text-[11px] text-gray-600 mt-1">No states for this country.</p>
          )}
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">
            Cities {citiesLoading ? "(loading…)" : `(${cities.length})`}
          </label>
          <select
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            size={8}
          >
            {!selectedIso2 && <option disabled>Select a country first</option>}
            {cities.map((city) => (
              <option key={city.name}>{city.name}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

export default function AdminOnboardingOptionsPage() {
  const [optionSets, setOptionSets] = useState<OptionSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { showError, showSuccess } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  useEffect(() => { if (success) showSuccess(success); }, [success, showSuccess]);

  const [editingType, setEditingType] = useState<string | null>(null);
  const [editedTypeKey, setEditedTypeKey] = useState<string>("");
  const [itemKind, setItemKind] = useState<ItemKind>("string");
  const [fields, setFields] = useState<string[]>([]);
  const [rows, setRows] = useState<any[]>([]);

  const [newType, setNewType] = useState("");
  const [newKind, setNewKind] = useState<ItemKind>("string");
  const [deleteTarget, setDeleteTarget] = useState<OptionSet | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<OptionSet[]>("/api/v1/onboarding/admin/options", { headers: adminHeaders() });
      setOptionSets(res.data);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const startEdit = (type: string, items: any[]) => {
    const schema = FIELD_SCHEMAS[type];
    const isString = STRING_TYPES.has(type) || (!schema && (items.length === 0 || typeof items[0] === "string"));

    setEditingType(type);
    setEditedTypeKey(type);
    setError(null);
    setSuccess(null);

    if (isString) {
      setItemKind("string");
      setFields([]);
      setRows(items.length ? [...items] : [""]);
    } else {
      const detectedFields = schema || (items.length ? Object.keys(items[0]) : ["value", "label", "description"]);
      setItemKind("object");
      setFields(detectedFields);
      setRows(items.length ? items.map((it) => ({ ...it })) : [Object.fromEntries(detectedFields.map((f) => [f, ""]))]);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEdit = (set: OptionSet) => startEdit(set.type, set.items || []);

  const handleNewCreate = () => {
    const type = newType.trim();
    if (!type) return;
    if (newKind === "string") {
      setItemKind("string");
      setFields([]);
      setRows([""]);
    } else {
      const defaultFields = ["value", "label", "description"];
      setItemKind("object");
      setFields(defaultFields);
      setRows([Object.fromEntries(defaultFields.map((f) => [f, ""]))]);
    }
    setEditingType(type);
    setEditedTypeKey(type);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setEditingType(null);
    setEditedTypeKey("");
    setItemKind("string");
    setFields([]);
    setRows([]);
    setNewType("");
  };

  const updateStringRow = (index: number, value: string) => {
    setRows((prev) => prev.map((r, i) => (i === index ? value : r)));
  };

  const updateObjectRow = (index: number, field: string, value: string) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const addRow = () => {
    if (itemKind === "string") {
      setRows((prev) => [...prev, ""]);
    } else {
      setRows((prev) => [...prev, Object.fromEntries(fields.map((f) => [f, ""]))]);
    }
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const moveRow = (index: number, dir: -1 | 1) => {
    setRows((prev) => {
      const target = index + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    if (!editingType) return;

    let items: any[];
    if (itemKind === "string") {
      items = (rows as string[]).map((v) => v.trim()).filter(Boolean);
    } else {
      items = (rows as Record<string, string>[])
        .map((row) => {
          const out: Record<string, any> = {};
          for (const f of fields) {
            const raw = (row[f] ?? "").toString().trim();
            if (NUMERIC_FIELDS.has(f)) {
              out[f] = raw === "" ? 0 : Number(raw);
            } else {
              out[f] = raw;
            }
          }
          return out;
        })
        .filter((row) => Object.values(row).some((v) => (typeof v === "string" ? v !== "" : v !== 0)));
    }

    setSaving(editingType);
    setError(null);
    try {
      if (!editedTypeKey.trim()) {
        setError("Type key cannot be empty.");
        setSaving(null);
        return;
      }
      await apiClient.put(`/api/v1/onboarding/options/${editingType}`, { type: editedTypeKey.trim(), items }, { headers: adminHeaders() });
      setSuccess(`Saved "${editedTypeKey.trim()}".`);
      handleCancel();
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(deleteTarget.type);
    try {
      await apiClient.delete(`/api/v1/onboarding/options/${deleteTarget.type}`, { headers: adminHeaders() });
      setDeleteTarget(null);
      setSuccess(`Deleted "${deleteTarget.type}".`);
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const isExisting = editingType !== null && optionSets.some((s) => s.type === editingType);

  return (
    <>
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 mb-6">
        <p className="text-gray-500 text-sm">
          Manage buyer/seller onboarding option sets (countries, cities, tags, activities, budgets,
          seller types, travel styles, personalities, pax tiers, languages). Changes apply
          immediately to onboarding flows.
        </p>
        <RefreshButton onRefresh={load} className="sm:ml-4" />
      </div>

      {error && <div className="mb-4 px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">{error}</div>}
      {success && <div className="mb-4 px-4 py-3 bg-green-900/40 border border-green-700 rounded-xl text-green-300 text-sm">{success}</div>}

      <GeoDataBrowser />

      {/* Editor */}
      {editingType !== null ? (
        <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white">
              {isExisting ? "Edit Option Set" : "New Option Set"}
            </h2>
          </div>
          
          <div className="mb-4">
            <label className="block text-xs text-gray-400 mb-1">Type key</label>
            <input
              value={editedTypeKey}
              onChange={(e) => setEditedTypeKey(e.target.value)}
              className="w-full md:w-1/2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            />
          </div>

          <div className="space-y-3 mb-4">
            {rows.map((row, index) => (
              <div key={index} className="flex items-center gap-2">
                {itemKind === "string" ? (
                  <input
                    value={row as string}
                    onChange={(e) => updateStringRow(index, e.target.value)}
                    placeholder="Value"
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  />
                ) : (
                  <div className="flex-1 grid gap-2" style={{ gridTemplateColumns: `repeat(${fields.length}, minmax(0, 1fr))` }}>
                    {fields.map((field) => (
                      <div key={field}>
                        <label className="block text-[10px] text-gray-500 mb-1">{fieldLabel(field)}</label>
                        {field === "description" ? (
                          <textarea
                            rows={1}
                            value={(row as Record<string, string>)[field] ?? ""}
                            onChange={(e) => updateObjectRow(index, field, e.target.value)}
                            placeholder={fieldLabel(field)}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-none"
                          />
                        ) : (
                          <input
                            type={NUMERIC_FIELDS.has(field) ? "number" : "text"}
                            value={(row as Record<string, string>)[field] ?? ""}
                            onChange={(e) => updateObjectRow(index, field, e.target.value)}
                            placeholder={fieldLabel(field)}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <button type="button" onClick={() => moveRow(index, -1)} disabled={index === 0}
                    title="Move up"
                    className="px-2 py-0.5 bg-gray-800 hover:bg-gray-700 text-gray-400 rounded text-xs disabled:opacity-30">
                    ↑
                  </button>
                  <button type="button" onClick={() => moveRow(index, 1)} disabled={index === rows.length - 1}
                    title="Move down"
                    className="px-2 py-0.5 bg-gray-800 hover:bg-gray-700 text-gray-400 rounded text-xs disabled:opacity-30">
                    ↓
                  </button>
                </div>
                <button type="button" onClick={() => removeRow(index)}
                  className="px-3 py-2 bg-red-900/30 hover:bg-red-900/60 text-red-400 rounded-lg text-xs transition-colors">
                  Remove
                </button>
              </div>
            ))}
          </div>

          <button type="button" onClick={addRow}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-xl text-sm text-gray-300 transition-colors mb-4">
            + Add item
          </button>

          <div className="flex gap-3">
            <button onClick={() => void handleSave()} disabled={saving === editingType}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors disabled:opacity-50">
              {saving === editingType ? "Saving…" : "Save"}
            </button>
            <button type="button" onClick={handleCancel}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl text-sm transition-colors">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 mb-6">
          <h2 className="text-sm font-semibold text-white mb-4">Add New Option Set</h2>
          <div className="grid grid-cols-3 gap-4 items-end">
            <div className="col-span-2">
              <label className="block text-xs text-gray-400 mb-1">Type key</label>
              <input value={newType} onChange={(e) => setNewType(e.target.value)}
                placeholder="e.g. dietary_preferences"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Item format</label>
              <select value={newKind} onChange={(e) => setNewKind(e.target.value as ItemKind)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50">
                <option value="string">Simple list (text values)</option>
                <option value="object">Structured (value / label / description)</option>
              </select>
            </div>
          </div>
          <button onClick={handleNewCreate} disabled={!newType.trim()}
            className="mt-4 px-5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors disabled:opacity-50">
            Create
          </button>
        </div>
      )}

      {/* Option sets table */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-800">
          <h2 className="text-sm font-semibold text-white">
            All Option Sets <span className="text-gray-500 font-normal ml-1">({optionSets.length})</span>
          </h2>
        </div>

        {loading ? (
          <div className="px-5 py-8 text-center text-gray-500 text-sm"><Spinner /></div>
        ) : optionSets.length === 0 ? (
          <div className="px-5 py-8 text-center text-gray-500 text-sm">No option sets found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-800">
                <tr>
                  <th className="text-left px-5 py-3 text-gray-500 font-medium text-xs">Type</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Items</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Updated</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {optionSets.map((set) => (
                  <tr key={set.type} className="hover:bg-gray-800/20 transition-colors">
                    <td className="px-5 py-3 text-white text-sm font-medium font-mono">{set.type}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{set.items?.length ?? 0} items</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{set.updated_at ? (parseInstant(set.updated_at) ?? new Date(set.updated_at)).toLocaleString() : <span className="text-gray-600">—</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => handleEdit(set)}
                          className="px-3 py-1 bg-indigo-900/40 hover:bg-indigo-900/70 text-indigo-300 rounded-lg text-xs transition-colors">
                          Edit
                        </button>
                        <button onClick={() => setDeleteTarget(set)} disabled={saving === set.type}
                          className="px-3 py-1 bg-red-900/30 hover:bg-red-900/60 text-red-400 rounded-lg text-xs transition-colors disabled:opacity-50">
                          {saving === set.type ? "…" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>

    {deleteTarget && (
      <ConfirmModal
        title="Delete Option Set"
        description={`"${deleteTarget.type}" will be permanently removed. Onboarding flows requesting this type will get a 404 until it is recreated.`}
        items={["Remove this option set from onboarding_options", "Buyer/seller onboarding screens using this type will break until recreated"]}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    )}
    </>
  );
}
